import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetRestaurantUsersApi } from './use-get-restaurant-users-api';

const endpoint = 'ums/v1/restaurants/users';
export const editRestaurantUserEndpointIdentifier = endpoint;

interface BodyType {
  name: string;
  phoneNumber: string;
  email: string;
  roleId: number;
  subEntities: {
    id: number;
    type: 'branch' | 'warehouse';
  }[];
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useEditRestaurantUserApi(restaurantId: string, userId: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const fullEndpoint = `${endpoint}/${restaurantId}/${userId}`;

  const { refetch: refetchRestaurantUsersApi } = useGetRestaurantUsersApi(restaurantId, { enabled: false });

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.put(fullEndpoint, body);

      // Refetch restaurant users
      refetchRestaurantUsersApi();

      toast({
        description: 'User has been updated successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
